import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Book, Trophy, Calendar, ArrowLeft, PencilSimple } from '@phosphor-icons/react';
import GlobalHeader from '../components/GlobalHeader';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export const Competitions = () => {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [userArticles, setUserArticles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/competitions`)
      .then(res => setCompetitions(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (competition) => {
    if (!user) {
      toast.error('Please login to submit your work');
      navigate('/login');
      return;
    }
    
    setSelectedCompetition(competition);
    setShowSubmitModal(true);
    
    // Fetch user's articles
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/articles?author_id=${user.user_id}&status=published`,
        { withCredentials: true }
      );
      setUserArticles(res.data.articles || res.data || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load your articles');
    }
  };

  const submitArticle = async (articleId) => {
    if (!selectedCompetition) return;
    
    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/competitions/${selectedCompetition.competition_id}/submit`,
        { article_id: articleId },
        { withCredentials: true }
      );
      toast.success('Entry submitted successfully!');
      setShowSubmitModal(false);
      setSelectedCompetition(null);
      
      // Refresh competitions to update entry count
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/competitions`);
      setCompetitions(res.data);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to submit entry');
    } finally {
      setSubmitting(false);
    }
  };

  const statusStyles = {
    active: 'bg-green-100 text-green-700 border-green-200',
    voting: 'bg-blue-100 text-blue-700 border-blue-200',
    closed: 'bg-gray-100 text-gray-700 border-gray-200',
    upcoming: 'bg-amber-100 text-amber-700 border-amber-200'
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <Link to="/" data-testid="back-button" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          <div className="flex items-center gap-3 mb-3">
            <Trophy size={40} weight="duotone" className="text-primary" />
            <h2 className="text-5xl md:text-6xl tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
              Writing Competitions
            </h2>
          </div>
          <p className="text-muted-foreground text-lg mb-12 max-w-3xl">
            Participate in monthly writing challenges, submit your best work, vote on entries, and earn recognition in the community.
          </p>

          {competitions.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-16 text-center">
              <Trophy size={48} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No active competitions right now</p>
            </div>
          ) : (
            <div className="space-y-6">
              {competitions.map((comp) => (
                <div
                  key={comp.competition_id}
                  data-testid={`competition-${comp.competition_id}`}
                  className="bg-card border border-border rounded-lg p-8 hover:border-primary transition-colors"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full border ${statusStyles[comp.status] || statusStyles.closed}`}>
                          {comp.status}
                        </span>
                        <span className="text-xs text-muted-foreground">Theme: {comp.theme}</span>
                      </div>
                      <h3 className="text-3xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
                        {comp.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">{comp.description}</p>
                    </div>
                  </div>

                  {comp.prize_description && (
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
                      <p className="text-sm"><Trophy size={16} weight="duotone" className="inline mr-2 text-primary" />{comp.prize_description}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar size={16} />
                        Ends: {new Date(comp.end_date).toLocaleDateString()}
                      </span>
                      <span>{comp.entries_count} submission{comp.entries_count !== 1 ? 's' : ''}</span>
                    </div>
                    {comp.status === 'active' && (
                      <button
                        onClick={() => handleSubmit(comp)}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold"
                      >
                        <PencilSimple size={18} weight="bold" />
                        Submit Entry
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Submit Entry Modal */}
      {showSubmitModal && selectedCompetition && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Submit Your Entry</h2>
                <p className="text-sm text-muted-foreground mt-1">{selectedCompetition.title}</p>
              </div>
              <button
                onClick={() => {
                  setShowSubmitModal(false);
                  setSelectedCompetition(null);
                }}
                className="p-2 hover:bg-muted rounded-lg"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Select one of your published articles to submit to this competition:
              </p>

              {userArticles.length === 0 ? (
                <div className="bg-muted/50 border border-border rounded-lg p-8 text-center">
                  <p className="text-muted-foreground mb-4">
                    You don't have any published articles yet.
                  </p>
                  <Link
                    to="/editor/new/article"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <PencilSimple size={18} weight="bold" />
                    Create New Article
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {userArticles.map((article) => (
                    <div
                      key={article.article_id}
                      className="bg-muted/30 border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold mb-1">{article.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {article.excerpt || article.content?.substring(0, 100)}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span>{article.content_type || 'article'}</span>
                            <span>•</span>
                            <span>{new Date(article.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => submitArticle(article.article_id)}
                          disabled={submitting}
                          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold disabled:opacity-50"
                        >
                          {submitting ? 'Submitting...' : 'Submit This'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedCompetition.rules && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mt-4">
                  <p className="text-sm font-semibold mb-2">Competition Rules:</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {selectedCompetition.rules}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Competitions;
